/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ClipboardIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { organizationService } from '../../services/api';
import { OrganizationDomain } from '../../types';
import ConfirmModal from '../../components/common/ConfirmModal';

export default function OrgDomainsTab({ orgId }: { orgId: string }) {
  const [domains, setDomains] = useState<OrganizationDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<OrganizationDomain | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await organizationService.getDomains(orgId);
      setDomains(res.data.domains || []);
    } catch {
      toast.error('Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      await organizationService.addDomain(orgId, newDomain.trim().toLowerCase());
      setNewDomain('');
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const verify = async (d: OrganizationDomain) => {
    setBusyId(d.id);
    try {
      await organizationService.verifyDomain(orgId, d.id);
      toast.success(`${d.domain} verified`);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await organizationService.deleteDomain(orgId, removeTarget.id);
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to remove domain');
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <div className="card space-y-6 max-w-2xl">
      <div>
        <h3 className="text-lg font-semibold text-white mb-1">Domains</h3>
        <p className="text-sm text-gray-400">
          Claim your company's email domains to route sign-ins to your identity provider. Prove ownership by adding a DNS
          TXT record, then click Verify.
        </p>
      </div>

      <form onSubmit={add} className="flex gap-2">
        <input
          required
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="acme.com"
          className="input flex-1 font-mono"
        />
        <button type="submit" disabled={adding} className="btn-primary disabled:opacity-50">
          {adding ? 'Adding…' : 'Add domain'}
        </button>
      </form>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : domains.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <GlobeAltIcon className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="font-medium">No domains claimed yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((d) => (
            <div key={d.id} className="border border-gray-700 rounded-lg p-4 bg-gray-950/40">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-white">{d.domain}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    d.status === 'verified' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}
                >
                  {d.status}
                </span>
              </div>
              {d.status === 'pending' && (
                <div className="space-y-2 text-sm">
                  <p className="text-gray-400">
                    Add this TXT record to <span className="font-mono">{d.domain}</span> (or{' '}
                    <span className="font-mono">_podstack-challenge.{d.domain}</span>):
                  </p>
                  <div className="flex items-center gap-2 bg-gray-800 rounded px-3 py-2 font-mono text-xs overflow-x-auto">
                    <span className="truncate flex-1 text-gray-200">{d.verification_token}</span>
                    <button
                      type="button"
                      onClick={() => copy(d.verification_token)}
                      className="shrink-0 text-gray-400 hover:text-white"
                    >
                      <ClipboardIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => verify(d)}
                    disabled={busyId === d.id}
                    className="btn-secondary btn-sm disabled:opacity-50"
                  >
                    {busyId === d.id ? 'Checking DNS…' : 'Verify'}
                  </button>
                </div>
              )}
              <div className="mt-2 text-right">
                <button onClick={() => setRemoveTarget(d)} className="text-xs text-red-400 hover:text-red-300">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={remove}
        isLoading={removing}
        title="Remove domain"
        message={`Remove ${removeTarget?.domain}? SSO login for this domain will stop working.`}
        confirmText="Remove domain"
        variant="danger"
      />
    </div>
  );
}
